import { ReactNode, useCallback, RefObject } from "react";
import { useShallow } from "zustand/shallow";

import { SortStateDescriptor } from "components/Filters/types";
import useAnalytics, { AnalyticsPage } from "hooks/useAnalytics";
import Box from "ds/components/Box";

import TableHeadColumn from "../Column";
import { TableColumnMeta } from "../../types";
import TableHeadInteractiveColumnTitle from "./Title";
import { useResizer } from "../../hooks/useResizer";
import useColumnSort from "../../hooks/useColumnSort";
import useTableWidth from "../../hooks/useTableWidth";
import { useColumnWidth } from "./useColumnWidth";
import ColumnActionsDropdown from "./ActionsDropdown";
import { COLUMN_CHANGED_ANALYTIC_EVENT } from "./constants";
import { TableGenericStore } from "../../hooks/useTableStore/types";

export type TableHeadInteractiveColumnProps = {
  column: TableColumnMeta;
  store: TableGenericStore;
  children?: ReactNode;
  stickyLeft?: boolean;
  stickyRight?: boolean;
  sortConfig?: Record<string, string>;
  sortCallback?: (sortDescriptor: SortStateDescriptor) => void;
  filtersStorageKey: string;
  tableRef?: RefObject<HTMLDivElement>;
  analyticsPage?: AnalyticsPage;
};

const TableHeadInteractiveColumn = ({
  column,
  store,
  children,
  stickyLeft,
  stickyRight,
  sortConfig,
  sortCallback,
  filtersStorageKey,
  tableRef,
  analyticsPage,
}: TableHeadInteractiveColumnProps) => {
  const {
    hideColumn,
    columnsSizes,
    setColumnSize,
    columnsOrder,
    reorderVisibleColumns,
    pinColumn,
  } = store(
    useShallow((state) => ({
      hideColumn: state.hideColumn,
      columnsSizes: state.columnsSizes,
      setColumnSize: state.setColumnSize,
      columnsOrder: state.columnsOrder,
      reorderVisibleColumns: state.reorderVisibleColumns,
      pinColumn: state.pinColumn,
    }))
  );

  const trackAnalytics = useAnalytics({ page: analyticsPage });
  const tableWidth = useTableWidth(tableRef, !!stickyLeft);

  const { savedColumnWidth, effectiveMaxWidth } = useColumnWidth(
    column,
    columnsSizes,
    setColumnSize,
    stickyLeft,
    tableWidth
  );

  const { ariaSort, handleSort, icon } = useColumnSort(
    sortConfig?.[column.id] || column.id,
    filtersStorageKey
  );

  const { ResizerControl, columnRef } = useResizer({
    width: savedColumnWidth,
    minWidth: column.minWidth,
    maxWidth: effectiveMaxWidth,
    onResizeClb: useCallback(
      (width: number) => {
        trackAnalytics(COLUMN_CHANGED_ANALYTIC_EVENT, {
          column: column.id,
          action: "resize",
        });
        setColumnSize(column.id, width);
      },
      [column.id, setColumnSize, trackAnalytics]
    ),
  });

  const onSort = useCallback(() => {
    handleSort(sortCallback);
  }, [handleSort, sortCallback]);

  return (
    <TableHeadColumn
      ref={columnRef}
      column={column}
      stickyLeft={stickyLeft}
      stickyRight={stickyRight}
      ariaSort={ariaSort}
    >
      {children}
      {!children && column.noHeader && <span className="sr-only">{column.title}</span>}
      {!children && !column.noHeader && (
        <Box gap="small" align="center" justify="between" fullHeight>
          <TableHeadInteractiveColumnTitle
            title={column.title}
            allowsSorting={column.allowsSorting}
            sortIcon={icon}
            onSort={onSort}
          />

          <Box align="center">
            <ColumnActionsDropdown
              column={column}
              columnsOrder={columnsOrder}
              stickyLeft={stickyLeft}
              hideColumn={hideColumn}
              pinColumn={pinColumn}
              reorderVisibleColumns={reorderVisibleColumns}
              analyticsPage={analyticsPage}
            />

            {column.resizable && ResizerControl}
          </Box>
        </Box>
      )}
    </TableHeadColumn>
  );
};

TableHeadInteractiveColumn.displayName = "DS.Table.Head.InteractiveColumn";

export default TableHeadInteractiveColumn;
